/**
 * Sorting the fee list, shared by the Cuotas screen and its Excel export so both show the
 * same order.
 */
export const FEE_SORT_KEYS = ["unit", "period", "amount", "dueDate", "status", "balance"] as const;
export type FeeSortKey = (typeof FEE_SORT_KEYS)[number];
export type SortDir = "asc" | "desc";

/** By urgency: what needs collecting first. */
const STATUS_ORDER: Record<string, number> = { OVERDUE: 0, PARTIAL: 1, PENDING: 2, PAID: 3 };

export interface SortableFee {
  amount: string | number | { toString(): string };
  dueDate: string | Date;
  period: string;
  status: string;
  balanceRemaining: number;
  unit: { unitNumber: string };
}

const num = (v: SortableFee["amount"]) => (typeof v === "number" ? v : parseFloat(v.toString()));
const time = (v: string | Date) => new Date(v).getTime();
/** "E2" before "E10". */
const unitCompare = (a: string, b: string) => a.localeCompare(b, "es", { numeric: true, sensitivity: "base" });

export function compareFees(a: SortableFee, b: SortableFee, key: FeeSortKey, dir: SortDir = "asc"): number {
  let c: number;
  switch (key) {
    case "period":
      c = a.period.localeCompare(b.period);
      break;
    case "amount":
      c = num(a.amount) - num(b.amount);
      break;
    case "dueDate":
      c = time(a.dueDate) - time(b.dueDate);
      break;
    case "status":
      c = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
      break;
    case "balance":
      c = a.balanceRemaining - b.balanceRemaining;
      break;
    default:
      c = 0;
  }
  // Ties (and the "unit" key) fall back to the unit, always ascending, so the order is stable.
  if (c !== 0) return dir === "desc" ? -c : c;
  const u = unitCompare(a.unit.unitNumber, b.unit.unitNumber);
  return key === "unit" && dir === "desc" ? -u : u;
}
